import { useState, useEffect, useRef } from "react";
import styles from "./CreateStoryPage.module.css";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { toast } from "react-toastify";
import useDashboardStore from "../../store/dashboardStore";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "heic"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "avi", "mkv"];

// Backend returns UTC with Z suffix → convert to local time for datetime-local input
const utcToLocalInput = (utcStr) => {
  if (!utcStr) return "";
  const d = new Date(utcStr); // parsed as UTC (Z suffix)
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
};

// datetime-local gives local time → convert to UTC ISO string for the backend (no Z, no ms)
const localInputToUtc = (localStr) => {
  if (!localStr) return null;
  // MySQL needs "2026-05-01T20:59:00" — no Z suffix, no milliseconds
  return new Date(localStr).toISOString().slice(0, 19);
};

const inferType = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (VIDEO_EXTENSIONS.includes(ext)) return "video";
  return null;
};

const CreateStoryPage = () => {
  const { setIsAllStoriesScreen, editStoryId } = useDashboardStore();
  const isEditMode = editStoryId !== null;

  // Mode toggle
  const [mode, setMode] = useState("media"); // "media" | "text"

  // Media state
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [existingContent, setExistingContent] = useState(null);

  // Text state
  const [textContent, setTextContent] = useState("");

  // Common fields
  const [expiresAt, setExpiresAt] = useState("");
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [searchPlan, setSearchPlan] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [storiesCount, setStoriesCount] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS);
        if (data.success) {
          setMemberships(data.memberships || []);
        }
      } catch (err) {
        console.error("Failed to fetch memberships", err);
      }
    };

    // Only need the count for new stories (sort_order = list length)
    const fetchStoriesCount = async () => {
      if (isEditMode) return;
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_STORIES_ADMIN);
        if (data.success) {
          setStoriesCount(data.stories?.length ?? 0);
        }
      } catch (err) {
        console.error("Failed to fetch stories count", err);
      }
    };

    fetchMemberships();
    fetchStoriesCount();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchStoryData();
    } else {
      resetForm();
    }
  }, [editStoryId]);

  const fetchStoryData = async () => {
    try {
      const { data } = await api.get(ENDPOINTS.GET_ALL_STORIES_ADMIN);
      if (data.success) {
        const story = data.stories.find((s) => s.id === editStoryId);
        if (!story) return;

        if (story.type === "text") {
          setMode("text");
          setTextContent(story.content || "");
        } else {
          setMode("media");
          setMediaType(story.type);
          setExistingContent(story.content);
          setMediaPreview(story.content);
        }

        setExpiresAt(utcToLocalInput(story.expires_at));
        setSelectedPlanIds(story.plan_ids || []);
      }
    } catch (err) {
      console.error("Failed to fetch story data", err);
      toast.error("Failed to load story data");
    }
  };

  const resetForm = () => {
    setMode("media");
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setExistingContent(null);
    setTextContent("");
    setExpiresAt("");
    setSelectedPlanIds([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const inferredType = inferType(file.name);
    if (!inferredType) {
      toast.error("Unsupported file type. Use image or video files.");
      return;
    }
    if (inferredType === "video") {
      const url = URL.createObjectURL(file);
      const vid = document.createElement("video");
      vid.preload = "metadata";
      vid.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        if (vid.duration > 120) {
          toast.error("Video must be 2 minutes or less.");
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }
        setMediaFile(file);
        setMediaType(inferredType);
        setExistingContent(null);
        setMediaPreview(URL.createObjectURL(file));
      };
      vid.src = url;
      return;
    }
    setMediaFile(file);
    setMediaType(inferredType);
    setExistingContent(null);
    setMediaPreview(URL.createObjectURL(file));
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setExistingContent(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePlanToggle = (id) => {
    setSelectedPlanIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const filteredMemberships = memberships.filter((m) =>
    (m.label || m.name || "")
      .toLowerCase()
      .includes(searchPlan.toLowerCase())
  );

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (mode === "media") {
      if (!mediaFile && !existingContent) {
        toast.error("Please select a media file.");
        return;
      }
    } else {
      if (!textContent.trim()) {
        toast.error("Please enter text content.");
        return;
      }
    }

    // Only block past expiry on create — on edit the admin may legitimately
    // update other fields while the story is already expired
    if (!isEditMode && expiresAt && new Date(expiresAt) <= new Date()) {
      toast.error("Expiry time must be in the future.");
      return;
    }

    setIsSubmitting(true);
    try {
      let contentUrl = existingContent || "";
      const storyType = mode === "text" ? "text" : mediaType;

      if (mode === "media" && mediaFile) {
        const formData = new FormData();
        formData.append("file", mediaFile);
        const uploadRes = await api.post(
          ENDPOINTS.UPLOAD_STORY_MEDIA,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (!uploadRes.data.success) {
          toast.error("Media upload failed");
          setIsSubmitting(false);
          return;
        }
        contentUrl = uploadRes.data.url;
      } else if (mode === "text") {
        contentUrl = textContent.trim();
      }

      const payload = {
        type: storyType,
        content: contentUrl,
        plan_ids: selectedPlanIds,
        expires_at: localInputToUtc(expiresAt), // always sent: UTC string or null
        ...(!isEditMode ? { sort_order: storiesCount } : {}),
      };

      const { data } = isEditMode
        ? await api.put(ENDPOINTS.UPDATE_STORY_BY_ID(editStoryId), payload)
        : await api.post(ENDPOINTS.CREATE_STORY, payload);

      if (data.success) {
        toast.success(
          data.message || (isEditMode ? "Story updated" : "Story created")
        );
        setIsAllStoriesScreen();
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Story submit error:", err);
      toast.error("Failed to save story");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      {/* App Bar */}
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {isEditMode ? "Update" : "Create"} Story
        </h2>
        <button
          className={styles.backButton}
          onClick={() => setIsAllStoriesScreen()}
        >
          ← All Stories
        </button>
      </div>

      <div className={styles.bodyContainer}>
        <div className={styles.leftSection}>

          {/* Mode toggle */}
          <div className={styles.liveSessionRow}>
            <label className={styles.inlineLabel}>Story Type</label>
            <div className={styles.toggleWrapper}>
              <button
                className={`${styles.toggleOption} ${
                  mode === "media" ? styles.activeToggle : ""
                }`}
                onClick={() => !isEditMode && setMode("media")}
                disabled={isEditMode || isSubmitting}
              >
                Media
              </button>
              <button
                className={`${styles.toggleOption} ${
                  mode === "text" ? styles.activeToggle : ""
                }`}
                onClick={() => !isEditMode && setMode("text")}
                disabled={isEditMode || isSubmitting}
              >
                Text
              </button>
            </div>
            {isEditMode && (
              <span className={styles.disabledNote}>
                Type cannot be changed when editing
              </span>
            )}
          </div>

          {/* Media upload */}
          {mode === "media" && (
            <div className={styles.imageSectionWrapper}>
              <label className={styles.imageLabel}>
                Media File
                <span
                  style={{
                    fontWeight: 400,
                    fontStyle: "italic",
                    color: "#999",
                    marginLeft: 6,
                    fontSize: 11,
                  }}
                >
                  (image or video)
                </span>
              </label>
              <div className={styles.featuredImageBox}>
                {mediaPreview && mediaType === "video" ? (
                  <video
                    src={mediaPreview}
                    className={styles.featuredVideo}
                    controls
                  />
                ) : mediaPreview ? (
                  <img
                    src={mediaPreview}
                    alt="Preview"
                    className={styles.featuredImage}
                  />
                ) : null}

                {mediaPreview && (
                  <button
                    type="button"
                    className={styles.removeImageButton}
                    onClick={handleRemoveMedia}
                    disabled={isSubmitting}
                    title="Remove media"
                  >
                    ✕
                  </button>
                )}

                <div className={styles.uploadLinkWrapper}>
                  <label className={styles.uploadLink}>
                    <span className={styles.plusIcon}>+</span>
                    {mediaPreview ? "Change Media" : "Choose Media"}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.heic,.mp4,.mov,.avi,.mkv"
                      hidden
                      onChange={handleFileChange}
                      disabled={isSubmitting}
                    />
                  </label>
                </div>
              </div>
              <p className={styles.uploadHint}>
                Accepted: jpg, jpeg, png, webp, heic, mp4, mov, avi, mkv
              </p>
            </div>
          )}

          {/* Text content */}
          {mode === "text" && (
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Text Content</label>
              <textarea
                className={styles.textArea}
                placeholder="Enter story text..."
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Expires At */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>
              Expires At
            </label>
            <input
              type="datetime-local"
              className={styles.textInput}
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={isSubmitting}
            />
            {expiresAt && (
              <button
                className={styles.clearButton}
                onClick={() => setExpiresAt("")}
                disabled={isSubmitting}
                title="Clear — story will never expire"
              >
                ✕ Clear
              </button>
            )}
          </div>
          {!expiresAt && (
            <p className={styles.uploadHint} style={{ marginTop: -16 }}>
              Leave empty for a lifetime story (never expires)
            </p>
          )}

          {/* Membership Plans */}
          <div className={styles.sectionsWrapper}>
            <div className={styles.sectionsCard}>
              <label className={styles.sectionsCardLabel}>
                Membership Plans
              </label>
              <p className={styles.sectionsCardHint}>
                Leave all unselected for a free / public story
              </p>

              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder="Filter plans..."
                  className={styles.searchInput}
                  value={searchPlan}
                  onChange={(e) => setSearchPlan(e.target.value)}
                  disabled={isSubmitting}
                />
                <span className={styles.searchIcon}>🔍</span>
              </div>

              <div className={styles.sectionList}>
                {filteredMemberships.length === 0 ? (
                  <p style={{ fontSize: 13, color: "#999", padding: "8px 10px" }}>
                    No plans found.
                  </p>
                ) : (
                  filteredMemberships.map((m) => {
                    const label = m.label || m.name || `Plan ${m.id}`;
                    return (
                      <div className={styles.sectionRow} key={m.id}>
                        <span className={styles.sectionLabel}>{label}</span>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={selectedPlanIds.includes(m.id)}
                          onChange={() => handlePlanToggle(m.id)}
                          disabled={isSubmitting}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            className={`${styles.submitButton} ${
              isSubmitting ? styles.submitting : ""
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update Story"
              : "Create Story"}
            <span className={styles.submitIcon}>
              {isSubmitting ? "↻" : "✓"}
            </span>
          </button>

        </div>
      </div>
    </div>
  );
};

export default CreateStoryPage;
